import { Box, Flex } from '@chakra-ui/react'
import { useCallback, useEffect, useMemo } from 'react'
import { Outlet } from 'react-router'
import { ToastContainer } from 'react-toastify'
import { useModalState } from '~/atoms/ModalState'
import { useUserState } from '~/atoms/UserState'
import MarkReadModal from '~/components/MarkReadModal'
import NewCallModal from '~/components/NewCallModal'
import NewThreadModal from '~/components/NewThreadModal'
import PreviewModal from '~/components/PreviewModal'
import { ElectronLogger } from '~/components/specialized/ElectronLogger'
import { useToastAlerts } from '~/hooks/toast/useToastAlerts'
import useElectronComms from '~/hooks/useElectronComms'
import { SequenceConfig, useKeySequences } from '~/hooks/useKeyboard'
import BannerManager from '~/pages/Home/Banners/BannerManager'
import { CallInProgressModal } from '~/pages/Home/Modals/CallInProgressModal/CallInProgressModal'
import ConfirmModal from '~/pages/Home/Modals/ConfirmModal/ConfirmModal'
import { NewExtensionSubscriptionModal } from '~/pages/Home/Modals/NewExtensionSubscriptionModal/NewExtensionSubscriptionModal'
import { NewFaxModal } from '~/pages/Home/Modals/NewFaxModal/NewFaxModal'
import { NewParkingLotModal } from '~/pages/Home/Modals/NewParkingLotModal/NewParkingLotModal'
import { TopNav } from '~/pages/Home/TopNav/TopNav'
import FeedbackReasonsModal from '~/pages/WebPhone/FeedbackReasonsModal'
import { initializeAnalytics } from '~/utils/analytics'
import { isElectron } from '~/utils/common'
import { easterEggSequences } from '~/utils/easterEggUtils'

import { MoveFaxModal } from './Modals/MoveFaxModal/MoveFaxModal'
import { NewSpeedDialModal } from './Modals/NewSpeedDialModal/NewSpeedDialModal'
import { SiteNavigation } from './SiteNavigation/SiteNavigation'

export const HomePage = () => {
  const { sessionUser: user, pbx, currentUserExtension } = useUserState()
  const { updateMenus } = useElectronComms()

  const handleShowDevTools = useCallback(() => {
    if (isElectron) {
      window.electron?.send('open-devtools')
    }
  }, [isElectron])

  const handleShowPopup = useCallback(() => {
    if (isElectron) {
      window.electron?.send('toggle-popup-window')
    }
  }, [isElectron])

  const sequences = useMemo(
    (): SequenceConfig[] => [
      {
        keys: [
          { code: 'KeyP', ctrlKey: true, altKey: true, shiftKey: true },
          { code: 'KeyP', ctrlKey: true, altKey: true, shiftKey: true },
        ],
        callback: handleShowPopup,
      },
      {
        keys: [
          { code: 'KeyD', ctrlKey: true, altKey: true, shiftKey: true },
          { code: 'KeyD', ctrlKey: true, altKey: true, shiftKey: true },
        ],
        callback: handleShowDevTools,
      },
      easterEggSequences,
    ],
    [handleShowDevTools],
  )

  useKeySequences(sequences)
  useToastAlerts()

  const {
    confirmModal: [, setConfirmModalState],
    faxModal: [faxModalState, setFaxModalState],
  } = useModalState()

  useEffect(() => {
    initializeAnalytics(user, currentUserExtension, pbx)
  }, [user, currentUserExtension, pbx])

  useEffect(() => {
    setFaxModalState({ ...faxModalState, open: false })
    setConfirmModalState(null)
    updateMenus({ enableAuthedMenuItems: true })
  }, [])

  return (
    <Box width="full" height="full" position="relative" overflowX="hidden">
      <ToastContainer
        autoClose={5000}
        pauseOnFocusLoss={false}
        hideProgressBar
        closeOnClick
        newestOnTop
      />
      <Flex direction="column" height="full">
        <BannerManager />
        <TopNav />
        <Flex direction="row" flex="1" minHeight={0}>
          <SiteNavigation />
          <div
            style={{
              display: 'flex',
              flex: 1,
              minHeight: 0,
              position: 'relative',
            }}
          >
            <Outlet />
          </div>
        </Flex>
      </Flex>

      <ElectronLogger />

      {/**
       * Modals
       */}

      <CallInProgressModal />
      <ConfirmModal />
      <MarkReadModal />
      <NewCallModal />
      <NewFaxModal />
      <MoveFaxModal />
      <NewParkingLotModal />
      <NewSpeedDialModal />
      <NewExtensionSubscriptionModal />
      <NewThreadModal />
      <PreviewModal />
      <FeedbackReasonsModal />
    </Box>
  )
}

export default HomePage
